/**
 * Utility functions for employee display formatting
 * Used across the application for consistent employee name and identifier display
 */

export interface EmployeeNameable {
  firstName: string;
  lastName: string;
  email?: string;
  userId?: string;
}

/**
 * Formats employee full name with email in parentheses
 * Used primarily for autocomplete dropdowns and detailed displays
 *
 * @param employee - Employee object with firstName, lastName, and email
 * @returns Formatted string: "First Last (email@example.com)"
 *
 * @example
 * getEmployeeDisplayName({ firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com' })
 * // "John Doe (john.doe@example.com)"
 */
export function getEmployeeDisplayName(employee: EmployeeNameable): string {
  const fullName = `${employee.firstName} ${employee.lastName}`;
  return employee.email ? `${fullName} (${employee.email})` : fullName;
}

/**
 * Formats employee short name (first and last name only)
 * Used for compact displays like assignee labels, activity logs, etc.
 *
 * @param employee - Employee object with firstName and lastName
 * @returns Formatted string: "First Last"
 *
 * @example
 * getEmployeeShortName({ firstName: 'John', lastName: 'Doe' })
 * // "John Doe"
 */
export function getEmployeeShortName(employee: EmployeeNameable): string {
  return `${employee.firstName} ${employee.lastName}`;
}

/**
 * Finds and formats employee name by userId from a list of employees
 * Returns null if employee is not found or userId is null
 *
 * @param employees - Array of employee objects
 * @param userId - User ID to search for (can be null)
 * @returns Formatted short name or null if not found or userId is null
 *
 * @example
 * const employees = [
 *   { userId: '123', firstName: 'John', lastName: 'Doe' },
 *   { userId: '456', firstName: 'Jane', lastName: 'Smith' }
 * ];
 * getEmployeeNameById(employees, '123') // "John Doe"
 * getEmployeeNameById(employees, '999') // null
 * getEmployeeNameById(employees, null) // null
 */
export function getEmployeeNameById(
  employees: EmployeeNameable[],
  userId: string | null
): string | null {
  if (!userId) return null;
  const employee = employees.find(emp => emp.userId === userId);
  return employee ? getEmployeeShortName(employee) : null;
}
